import React, { useContext, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { isNullOrUndefined } from '@utils/utils';
import { Button } from 'primereact/button';
import { ApiContext } from '@api/api';
import { Toast } from 'primereact/toast';
import { Checkbox } from 'primereact/checkbox';
import styled from 'styled-components';
import Label from '@components/obsolete/Form/Label/Label.tsx';
import * as yup from 'yup';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { getCrnFormatDescription, validateCrn } from '@utils/crnValidation.ts';
import useForm from '@hooks/useForm/useForm.tsx';
import InputCountry from '@components/framework/input/InputCountry/InputCountry.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { findErrorMessage } from '@utils/validationUtils.ts';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import InputTimezone from '@components/ringil3/Input/InputTimezone/InputTimezone.tsx';
import { useApplyResolvedTimezone } from '@hooks/useResolveTimezone/useResolveTimezone';

interface ComponentProps {}

interface FormData {
  companyName?: string;
  crn?: string;
  city?: string;
  zipCode?: string;
  streetNr?: string;
  vat?: string;
  country?: string;
  adminFirstName?: string;
  adminLastName?: string;
  adminEmail?: string;
  phone?: string;
  onboardingPopulate: boolean;
  timeZone: string;
  defaultTimezoneId: string;
}

const CreateCompany: React.FC<ComponentProps> = () => {
  const { tr } = useTranslationLgs();

  const [bypassCrnValidation, setBypassCrnValidation] = useState(false);

  const { createFirstCompanyRegistration, getCompanyFromAresByICO } = useContext(ApiContext);

  const companySchema = yup.object().shape({
    country: yup.string().required(tr('countryCode.required', 'Country is required')),
    crn: yup
      .string()
      .required(tr('validationCrn.required', 'CRN is required'))
      .test('crn-format', function(value) {
        if (bypassCrnValidation) return true;
        const { country } = this.parent;
        if (!value || !country) return true;
        const isValid = validateCrn(value, country);
        if (!isValid) {
          const formatDesc = getCrnFormatDescription(country, tr);
          return this.createError({
            message: formatDesc
              ? tr('validationCrn.crnInvalidFormat', `Invalid CRN format (expected format: {{format}})`, { format: formatDesc })
              : tr('validationCrn.crnInvalid', `Invalid CRN format`),
          });
        }
        return true;
      }),
    companyName: yup.string().required(tr('companyName.required', 'Company name is required')),
    city: yup.string().required(tr('city.required', 'City is required')),
    zipCode: yup.string().required(tr('zipCode.required', 'Zip code is required')),
    streetNr: yup.string().required(tr('streetNr.required', 'Street and number is required')),
    vat: yup.string().required(tr('vat.required', 'Vatr is required')),
    adminFirstName: yup.string().required(tr('adminFirstName.required', 'Admin first name is required')),
    adminLastName: yup.string().required(tr('adminLastName.required', 'Admin last name is required')),
    adminEmail: yup.string().email(tr('adminEmail.invalid', 'Invalid email format')).required(tr('adminEmail.required', 'Admin email is required')),
    phone: yup.string().optional(),
  });

  const createInitialData = (): FormData => {
    return {
      country: 'CZE',
      crn: '',
      onboardingPopulate: false,
      timeZone: 'Europe/Prague',
      defaultTimezoneId: 'Europe/Prague',
    };
  };


  const companyForm = useForm<FormData>(
    companySchema,
    createInitialData(),
    d => createCustomer(d),
    false,
    false,
    null,
    'scroll',
  );

  const onChange = (property: any, value: any) => {
    companyForm.setFieldValue(property, value);
  };

  useApplyResolvedTimezone(
    { countryCode3: companyForm.values.country, city: companyForm.values.city, postalCode: companyForm.values.zipCode },
    tz => companyForm.setFieldValue(companyForm.names.defaultTimezoneId, tz),
  );

  const onLoadFromAres = () => {
    getCompanyFromAresByICO(companyForm.values.crn, d => {
      onChange('zipCode', d.zipCode);
      onChange('companyName', d.name);
      onChange('city', d.city);
      onChange('streetNr', d.street);
      onChange('crn', d.ico);
      onChange('vat', d.dic);
      onChange('country', 'CZE');
    });
  };

  const toast = useRef<Toast | null>(null);

  const createCustomer = (formData: FormData) => {
    createFirstCompanyRegistration(formData as unknown as any, () => {
      toast?.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Customer invited...',
      });
    });
  };

  return (
    <>
      <div className="p-fluid p-formgrid p-grid">
        <div className="p-col-12 p-md-6 p-lg-6">
          <Toast ref={toast} />
          <h1>Create company</h1>

          <div className="flex-col gap-2">
            <Field
              label={tr(`Registration.registrationFormCountry`, `Country`)}
              required
              errorMessage={findErrorMessage(companyForm.validationErrors, `countryCode`, tr)}
            >
              <InputCountry
                value={companyForm.values.country}
                onChange={v => companyForm.setFieldValue(companyForm.names.country, v)}
              />
            </Field>
            <Field
              label={tr(`Registration.registrationFormCrn`, `CRN`)}
              required
              errorMessage={findErrorMessage(companyForm.validationErrors, `crn`, tr)}
            >
              <InputText
                value={companyForm.values.crn}
                onChange={v => {
                  companyForm.setFieldValue(companyForm.names.crn, v.target.value);
                }}
              />
            </Field>
            <div className="flex flex-row pt-2 justify-between">
              <RingilButton
                mode="filled-rectangle"
                label={'Load from ARES'}
                onClick={onLoadFromAres}
                disabled={isNullOrUndefined(companyForm.values.crn)}
              />
              <RingilInputSwitch
                label={tr('Registration.bypassCrnValidation', 'Bypass CRN validation')}
                value={bypassCrnValidation}
                onChange={value => setBypassCrnValidation(value)}
              />
            </div>
            <Field
              label={tr(`Registration.registrationFormCompanyName`, `Company Name`)}
              required
              errorMessage={findErrorMessage(companyForm.validationErrors, `companyName`, tr)}
            >
              <InputText
                value={companyForm.values.companyName}
                onChange={v => {
                  companyForm.setFieldValue(companyForm.names.companyName, v.target.value);
                }}
              />
            </Field>
            <Field
              label={tr(`Registration.registrationFormCity`, `City`)}
              required
              errorMessage={findErrorMessage(companyForm.validationErrors, `city`, tr)}
            >
              <InputText
                value={companyForm.values.city}
                onChange={v => {
                  companyForm.setFieldValue(companyForm.names.city, v.target.value);
                }}
              />
            </Field>
            <Field
              label={tr(`Registration.registrationFormZipCode`, `ZIP Code`)}
              required
              errorMessage={findErrorMessage(companyForm.validationErrors, `zipCode`, tr)}
            >
              <InputText
                value={companyForm.values.zipCode}
                onChange={v => {
                  companyForm.setFieldValue(companyForm.names.zipCode, v.target.value);
                }}
              />
            </Field>
            <Field
              label={tr(`Registration.registrationFormStreetNr`, `Street and number`)}
              required
              errorMessage={findErrorMessage(companyForm.validationErrors, `streetNr`, tr)}
            >
              <InputText
                value={companyForm.values.streetNr}
                onChange={v => {
                  companyForm.setFieldValue(companyForm.names.streetNr, v.target.value);
                }}
              />
            </Field>
            <Field
              label={tr(`Registration.registrationFormVat`, `VAT`)}
              required
              errorMessage={findErrorMessage(companyForm.validationErrors, `vat`, tr)}
            >
              <InputText
                value={companyForm.values.vat}
                onChange={v => {
                  companyForm.setFieldValue(companyForm.names.vat, v.target.value);
                }}
              />
            </Field>
            <COHeadline
              title={tr(`Registration.titleAdministrationPerrson`, 'Administration person')}
              variant={'h3'}
              className="pt-2"
            />
            <Field
              label={tr(`Registration.registrationFormFirstName`, `First Name`)}
              required
              errorMessage={findErrorMessage(companyForm.validationErrors, `adminFirstName`, tr)}
            >
              <InputText
                value={companyForm.values.adminFirstName}
                onChange={v => {
                  companyForm.setFieldValue(companyForm.names.adminFirstName, v.target.value);
                }}
              />
            </Field>
            <Field
              label={tr(`Registration.registrationFormLastName`, `Last Name`)}
              required
              errorMessage={findErrorMessage(companyForm.validationErrors, `adminLastName`, tr)}
            >
              <InputText
                value={companyForm.values.adminLastName}
                onChange={v => {
                  companyForm.setFieldValue(companyForm.names.adminLastName, v.target.value);
                }}
              />
            </Field>
            <Field
              label={tr(`Registration.registrationFormEmail`, `Email`)}
              required
              errorMessage={findErrorMessage(companyForm.validationErrors, `adminEmail`, tr)}
            >
              <InputText
                value={companyForm.values.adminEmail}
                onChange={v => {
                  companyForm.setFieldValue(companyForm.names.adminEmail, v.target.value);
                }}
              />
            </Field>
            <Field
              label={tr(`Registration.registrationFormPhone`, `Phone`)}
              required={false}
              errorMessage={findErrorMessage(companyForm.validationErrors, `phone`, tr)}
            >
              <InputText
                value={companyForm.values.phone}
                onChange={v => {
                  companyForm.setFieldValue(companyForm.names.phone, v.target.value);
                }}
              />
            </Field>
            <div className="p-field ">
              <Label title="Timezone - nemenit"></Label>
              <InputText value={companyForm.values.timeZone}
                         onChange={e => onChange('timeZone', (e.target as HTMLInputElement).value)} />
            </div>
            <div>
              <InputTimezone
                value={companyForm.values.defaultTimezoneId}
                onChange={v => companyForm.setFieldValue(companyForm.names.defaultTimezoneId, v)}
              />
            </div>
            <div className="p-field ">
              <CheckboxRow>
                <Checkbox checked={companyForm.values.onboardingPopulate}
                          onChange={e => onChange('onboardingPopulate', e.checked)} />
                <CheckboxLabel>Populate sample data</CheckboxLabel>
              </CheckboxRow>
              <small>Populate sample data for onboarding</small>
            </div>
            <div className="p-grid p-justify-end">
              <div className={'p-col p-lg-4 p-md-6 p-sm-12'}>
                <Button label="Save" onClick={() => companyForm.validateAndSend()} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const CheckboxRow = styled.div`
  display: flex;
  gap: 0.2rem;
`;

const CheckboxLabel = styled.div`
  display: flex;
  font-weight: bold;
`;

export default CreateCompany;
